import { useTranslation } from "react-i18next";
import { Edit, X, Save, Loader2, Image as ImageIcon, Eye } from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatLocalDateTime } from "../../lib/dateUtils";
import { API_URL } from "../../lib/config";
import { uploadImage } from "../../lib/api";
import { toast } from "sonner";
import { ImageCarousel } from "../ImageCarousel";
import { MarkdownText } from "../MarkdownText";
import { MarkdownHelp } from "../MarkdownHelp";

export function InfoSection({ consulta, editing, onStartEdit, form, onFieldChange, saving, onSave, onCancel }) {
  const { t } = useTranslation();
  const canEdit = consulta.status === "draft";
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) { toast.error(t("consultaDetail.formatNotAllowed")); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error(t("consultaDetail.fileTooLarge")); return; }
    const currentUrls = form?.imageUrls || [];
    if (currentUrls.length >= 6) { toast.error(t("consultaDetail.maxImagesReached")); return; }
    setUploadingImage(true);
    try {
      const response = await uploadImage(file);
      const newUrl = response.data.url;
      const updatedUrls = [...currentUrls, newUrl];
      onFieldChange("imageUrls", updatedUrls, { imageUrl: updatedUrls[0] });
      toast.success(t("consultaDetail.imageUploaded"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("consultaDetail.imageUploadError"));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeConsultaImage = (imgIdx) => {
    const currentUrls = [...(form?.imageUrls || [])];
    currentUrls.splice(imgIdx, 1);
    onFieldChange("imageUrls", currentUrls, { imageUrl: currentUrls[0] || null });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading font-semibold text-slate-900">{t('consultaDetail.consultaInfo')}</h2>
        {canEdit && !editing && (
          <Button size="sm" onClick={onStartEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-info-btn">
            <Edit className="h-4 w-4 mr-1" />{t('common.edit')}
          </Button>
        )}
        {canEdit && editing && (
          <div className="flex gap-2">
            <Button size="sm" onClick={onCancel} className="bg-slate-600 hover:bg-slate-700" data-testid="cancel-edit-btn">
              <X className="h-4 w-4 mr-1" />{t('common.cancel')}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-edit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />{t('common.save')}</>}
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1 block">{t('consultaDetail.consultaTitle')} *</Label>
            <Input value={form.title} onChange={(e) => onFieldChange('title', e.target.value)} className="h-11"
              placeholder={t('consultaDetail.consultaTitlePlaceholder')} data-testid="edit-title-input" />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1 block">{t('consultaDetail.description')} <span className="ml-1"><MarkdownHelp /></span></Label>
            <textarea value={form.description} onChange={(e) => onFieldChange('description', e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-lg resize-none h-24"
              placeholder={t('consultaDetail.descriptionPlaceholder')} data-testid="edit-description-input" />
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1 block">{t('consultaDetail.whoCanParticipate')}</Label>
            <textarea value={form.eligibility} onChange={(e) => onFieldChange('eligibility', e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-lg resize-none h-20"
              placeholder="Ex: Tots els veins majors de 16 anys empadronats al municipi..." data-testid="edit-eligibility-input" />
          </div>
          {/* Consulta Image */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1 block">{t('consultaDetail.imageLabel')}</Label>
            <p className="text-xs text-slate-500 mb-2">{t('newConsulta.imageDesc')}</p>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3 pt-1 pr-1">
                {(form.imageUrls?.length ? form.imageUrls : (form.imageUrl ? [form.imageUrl] : [])).map((url, imgIdx) => (
                  <div key={imgIdx} className="relative inline-block">
                    <img src={`${API_URL}${url}`} alt={`Imatge votació ${imgIdx + 1}`} className="h-24 w-32 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                    <button type="button" onClick={() => removeConsultaImage(imgIdx)}
                      className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-md cursor-pointer" data-testid={`remove-consulta-image-${imgIdx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(form.imageUrls?.length || (form.imageUrl ? 1 : 0)) < 6 && (
                  <div>
                    <input ref={imageInputRef} type="file" accept=".jpg,.jpeg,.png" className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0])} data-testid="consulta-image-input-edit" />
                    <Button type="button" variant="outline" onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage} className="h-24 w-32 border-dashed flex flex-col items-center justify-center gap-1"
                      data-testid="upload-consulta-image-edit-btn">
                      {uploadingImage ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        : <><ImageIcon className="h-5 w-5 text-slate-400" /><span className="text-xs text-slate-500">+ {t('consultaDetail.uploadImage')}</span></>}
                    </Button>
                  </div>
                )}
              </div>
              {(form.imageUrls?.length || (form.imageUrl ? 1 : 0)) > 0 && (
                <p className="text-xs text-slate-400">{(form.imageUrls?.length || (form.imageUrl ? 1 : 0))}/6</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('consultaDetail.startDateTime')} *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={form.startDate} onChange={(e) => onFieldChange('startDate', e.target.value)} className="h-10" data-testid="edit-start-date" />
                <Input type="time" value={form.startTime} onChange={(e) => onFieldChange('startTime', e.target.value)} className="h-10" data-testid="edit-start-time" />
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('consultaDetail.endDateTime')} *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={form.endDate} onChange={(e) => onFieldChange('endDate', e.target.value)} className="h-10" data-testid="edit-end-date" />
                <Input type="time" value={form.endTime} onChange={(e) => onFieldChange('endTime', e.target.value)} className="h-10" data-testid="edit-end-time" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.consultaTitle')}</p>
            <p className="text-lg font-semibold text-slate-900">{consulta.title}</p>
          </div>
          {(consulta.image_urls?.length || consulta.image_url) && (
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.imageLabel')}</p>
              <ImageCarousel
                images={(consulta.image_urls?.length ? consulta.image_urls : (consulta.image_url ? [consulta.image_url] : [])).map(u => `${API_URL}${u}`)}
                alt={consulta.title}
                maxHeight="max-h-64"
                className="rounded-lg border border-slate-200"
              />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.description')}</p>
            <MarkdownText className="text-slate-700">{consulta.description || t('consultaDetail.noDescription')}</MarkdownText>
          </div>
          {consulta.eligibility && (
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.whoCanParticipate')}</p>
              <p className="text-slate-700">{consulta.eligibility}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.start')}</p>
              <p className="text-slate-700">{formatLocalDateTime(consulta.start_date, consulta.entity_timezone)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">{t('consultaDetail.end')}</p>
              <p className="text-slate-700">{formatLocalDateTime(consulta.end_date, consulta.entity_timezone)}</p>
            </div>
          </div>
          {/* Preview button */}
          <div className="pt-2">
            <Button size="sm" onClick={() => window.open(`/vote/${consulta.id}`, '_blank')} data-testid="preview-voting-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white border-0">
              <Eye className="h-4 w-4 mr-2" />{t('consultaDetail.previewVoting')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
